import { useState, useEffect, useRef } from 'react';
import {
  Flex,
  Box,
  useColorModeValue
} from '@chakra-ui/react';
import BottomNav from './BottomNav/BottomNav';
import About from './sections/About';
import History from './sections/History';
import WhyPiePOS from './sections/WhyPiePOS';
import Pricing from './sections/Pricing';
import OwnADomain from './sections/OwnADomain';
import Demo from './sections/Demo';
import Contact from './sections/Contact';

function LandingPage() {
  const [activeSection, setActiveSection] = useState('about');
  // console.log("active section is: ",activeSection);

  // Create refs for each section
  const sectionRefs = {
    about: useRef(null),
    history: useRef(null),
    whypiepos: useRef(null),
    pricing: useRef(null),
    ownadomain: useRef(null),
    demo: useRef(null),
    contact: useRef(null)
  };

  // Color mode values matching your existing components
  const bgColor = useColorModeValue('#fafaf7', 'gray.800');
  const accentColor = useColorModeValue('yellow.400', 'yellow.500');

  // Function to scroll to a specific section
  const scrollToSection = (sectionId) => {
    const sectionRef = sectionRefs[sectionId];
    if (sectionRef && sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Function to detect which section is currently in view
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.getAttribute('data-section');
          setActiveSection(sectionId);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line
  }, []);

  return (
    <Box
      position="relative"
      width="100vw"
      minHeight="100vh"
      // height="100%"
      bg={bgColor}
      overflow="hidden"
    >
      {/* Primary Decorative Elements - Top Section */}
      <Box
        display={{ base: 'none', md: 'block' }}
        position="absolute"
        top="5%"
        left="3%"
        w="200px"
        h="200px"
        bg="yellow.400"
        borderRadius="50% 30% 70% 40%"
        opacity="0.6"
        zIndex="0"
        animation="float 6s ease-in-out infinite"
        sx={{
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(5deg)' }
          }
        }}
      />

      <Box
        display={{ base: 'none', lg: 'block' }}
        position="absolute"
        top="15%"
        right="8%"
        w="150px"
        h="150px"
        bg="yellow.300"
        borderRadius="40% 60% 30% 70%"
        opacity="0.5"
        zIndex="0"
        animation="float 8s ease-in-out infinite reverse"
        sx={{
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-15px) rotate(-3deg)' }
          }
        }}
      />

      {/* Secondary Decorative Elements - Middle Section */}
      <Box
        display={{ base: 'none', md: 'block' }}
        position="absolute"
        top="40%"
        left="10%"
        w="120px"
        h="120px"
        bg="yellow.200"
        borderRadius="60% 40% 80% 20%"
        opacity="0.7"
        zIndex="0"
        animation="pulse 4s ease-in-out infinite"
        sx={{
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.1)' }
          }
        }}
      />

      {/* Bottom Section Decorative Elements */}

      <Box
        display={{ base: 'none', lg: 'block' }}
        position="absolute"
        bottom="15%"
        right="12%"
        w="100px"
        h="100px"
        bg="yellow.200"
        borderRadius="50% 50% 80% 20%"
        opacity="0.6"
        zIndex="0"
        animation="float 6s ease-in-out infinite reverse"
        sx={{
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-18px)' }
          }
        }}
      />

      {/* Decorative Lines and Shapes */}
      <Box
        display={{ base: 'none', md: 'block' }}
        position="absolute"
        top="25%"
        right="0"
        w="250px"
        h="4px"
        bg={accentColor}
        borderRadius="full"
        transform="rotate(45deg)"
        opacity="0.7"
        zIndex="0"
      />

      <Box
        display={{ base: 'none', lg: 'block' }}
        position="absolute"
        top="60%"
        left="0"
        w="180px"
        h="3px"
        bg="orange.400"
        borderRadius="full"
        transform="rotate(-30deg)"
        opacity="0.6"
        zIndex="0"
      />

      {/* Floating Arrow Decorations */}
      <Box
        display={{ base: 'none', md: 'block' }}
        position="absolute"
        top="30%"
        right="15%"
        fontSize="60px"
        color={accentColor}
        opacity="0.6"
        zIndex="0"
        transform="rotate(25deg)"
        animation="bounce 3s ease-in-out infinite"
        sx={{
          '@keyframes bounce': {
            '0%, 100%': { transform: 'rotate(25deg) translateY(0px)' },
            '50%': { transform: 'rotate(25deg) translateY(-10px)' }
          }
        }}
      >
        ↘
      </Box>

      <Box
        display={{ base: 'block', lg: 'block' }}
        position="absolute"
        bottom="35%"
        left="20%"
        fontSize="80px"
        color="orange.400"
        opacity="0.5"
        zIndex="0"
        transform="rotate(-15deg)"
        animation="bounce 4s ease-in-out infinite reverse"
        sx={{
          '@keyframes bounce': {
            '0%, 100%': { transform: 'rotate(-15deg) translateY(0px)' },
            '50%': { transform: 'rotate(-15deg) translateY(-12px)' }
          }
        }}
      >
        ↗
      </Box>

      {/* Additional Small Decorative Elements */}
      <Box
        display={{ base: 'none', xl: 'block' }}
        position="absolute"
        top="50%"
        left="2%"
        w="60px"
        h="60px"
        bg="yellow.100"
        borderRadius="full"
        opacity="0.8"
        zIndex="0"
        animation="pulse 3s ease-in-out infinite"
        sx={{
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.2)' }
          }
        }}
      />

      <Box
        display={{ base: 'none', xl: 'block' }}
        position="absolute"
        top="70%"
        right="3%"
        w="80px"
        h="80px"
        bg="red.200"
        borderRadius="30% 70% 70% 30%"
        opacity="0.6"
        zIndex="0"
        animation="float 5s ease-in-out infinite"
        sx={{
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-15px) rotate(10deg)' }
          }
        }}
      />

      {/* Mobile-specific smaller decorations */}
      <Box
        display={{ base: 'block', md: 'none' }}
        position="absolute"
        top="10%"
        right="5%"
        w="40px"
        h="40px"
        bg={accentColor}
        borderRadius="full"
        opacity="0.6"
        zIndex="0"
        animation="pulse 3s ease-in-out infinite"
        sx={{
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.1)' }
          }
        }}
      />

      <Box
        display={{ base: 'block', md: 'none' }}
        position="absolute"
        bottom="20%"
        left="8%"
        w="30px"
        h="30px"
        bg="orange.300"
        borderRadius="50% 50% 0 50%"
        opacity="0.7"
        zIndex="0"
        animation="float 4s ease-in-out infinite"
        sx={{
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-8px)' }
          }
        }}
      />

      {/* Gradient Overlays for Better Visual Depth */}
      {/*<Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgGradient="radial(circle at 20% 20%, rgba(255, 214, 10, 0.1) 0%, transparent 50%)"
        zIndex="0"
        pointerEvents="none"
      />

      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgGradient="radial(circle at 80% 80%, rgba(255, 165, 0, 0.08) 0%, transparent 50%)"
        zIndex="0"
        pointerEvents="none"
      />*/}

      {/* Main Content Container */}
      <Flex
        direction="column"
        height="100vh"
        width="100%"
        position="relative"
        zIndex="1"
      >
        <Box flex="1" overflow="auto" position="relative">
          {/* All sections rendered in sequence */}
          <Box ref={sectionRefs.about} data-section="about">
            <About />
          </Box>

          <Box ref={sectionRefs.whypiepos} data-section="whypiepos">
            <WhyPiePOS />
          </Box>

          <Box ref={sectionRefs.pricing} data-section="pricing">
            <Pricing />
          </Box>

          <Box ref={sectionRefs.demo} data-section="demo">
            <Demo />
          </Box>

          <Box ref={sectionRefs.ownadomain} data-section="ownadomain">
            <OwnADomain />
          </Box>

          <Box ref={sectionRefs.history} data-section="history">
            <History />
          </Box>

          <Box ref={sectionRefs.contact} data-section="contact">
            <Contact />
          </Box>
        </Box>

        {/* Bottom Navigation */}
        <Box
          position="fixed"
          bottom="0"
          left="0"
          right="0"
          zIndex={1000}
          bg="transparent"
          pointerEvents="none"
        >
          <Box pointerEvents="auto">
            <BottomNav
              activeSection={activeSection}
              onSectionClick={scrollToSection}
            />
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}

export default LandingPage;